import { Phone, MapPin, CheckCircle, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const ShoreditchPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Recovery Shoreditch | Vehicle Transport E1, E2 | 24/7 Breakdown Service"
        description="Fast car recovery in Shoreditch E1, E2. 30-minute response. 24/7 breakdown service, vehicle transport, towing. Local Shoreditch service. Call 07492 639223 now."
        keywords="car recovery shoreditch, breakdown service shoreditch, car recovery e1, car recovery e2, shoreditch vehicle transport, shoreditch towing, old street car recovery"
      />
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              📍 Local Shoreditch Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Car Recovery & Vehicle Transport in Shoreditch
            </h1>
            <p className="text-xl text-purple-100 mb-8">
              Fast 30-minute car recovery across Shoreditch E1, E2. Available 24/7 for breakdowns, vehicle transport, and emergency recovery in Shoreditch, Old Street, Hoxton, and surrounding areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-purple-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-purple-900 text-lg px-8 py-6">
                  Get Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">30 Min</p>
              <p className="text-sm text-slate-600">Avg Response</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">24/7</p>
              <p className="text-sm text-slate-600">Always Available</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">Local</p>
              <p className="text-sm text-slate-600">Shoreditch Based</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">E1, E2</p>
              <p className="text-sm text-slate-600">Full Coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Car Recovery Services in Shoreditch
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Based locally in East London, Ovawell Autos provides the fastest car recovery and vehicle transport service in Shoreditch. Our average 30-minute response time across E1 and E2 postcodes means you're never stuck waiting for help. From Old Street to Hoxton, we know every street in Shoreditch and can reach you faster than national recovery services.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Shoreditch Areas We Cover
            </h3>
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              {[
                "Old Street",
                "Shoreditch High Street",
                "Hoxton",
                "Shoreditch E1",
                "Shoreditch E2",
                "Brick Lane",
                "Spitalfields",
                "Liverpool Street",
                "Columbia Road",
                "Curtain Road",
                "Great Eastern Street",
                "Kingsland Road"
              ].map((area, index) => (
                <div key={index} className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="text-slate-700">{area}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Our Shoreditch Services
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "24/7 breakdown recovery",
                "Vehicle transportation",
                "Accident recovery",
                "Jump start services",
                "Car auction transport",
                "Emergency roadside assistance",
                "Flat tyre recovery",
                "Out of fuel assistance",
                "Non-running vehicle transport",
                "Keys locked in car",
                "Battery problems",
                "Urgent towing service"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Ovawell Autos in Shoreditch?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Clock className="w-8 h-8 text-purple-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Local Response - 30 Minutes Average</h4>
                      <p className="text-slate-600">
                        We're based in East London and know Shoreditch inside out. Our local knowledge means we reach you faster - Old Street in 15 minutes, Hoxton in 20, anywhere in E1/E2 within 30 minutes on average.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-8 h-8 text-purple-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">We Know Shoreditch Roads</h4>
                      <p className="text-slate-600">
                        From busy Shoreditch High Street to narrow side streets off Brick Lane, we navigate all areas. We know the one-way systems, restricted zones, and best access points.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-purple-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">24/7 Service - Fixed Pricing</h4>
                      <p className="text-slate-600">
                        Available every day of the year including bank holidays. Same transparent pricing 24/7 - no night surcharges. Get a quote before we dispatch.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Common Breakdown Spots in Shoreditch
            </h3>
            <p className="text-slate-700 mb-4">
              We regularly help customers at these busy Shoreditch locations:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 mb-8">
              <li><strong>Old Street Roundabout:</strong> High-traffic area - we prioritize fast response</li>
              <li><strong>Shoreditch High Street:</strong> Busy main road with restricted parking</li>
              <li><strong>Great Eastern Street:</strong> Major route - quick recovery needed</li>
              <li><strong>Brick Lane area:</strong> Narrow streets - we know best access points</li>
              <li><strong>Liverpool Street Station:</strong> High-pressure location - fast service</li>
              <li><strong>Hoxton Square area:</strong> Residential roads - careful recovery</li>
            </ul>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              How to Book Car Recovery in Shoreditch
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-purple-600">1</span>
                  </div>
                  <h4 className="font-bold mb-2">Call or Quote</h4>
                  <p className="text-sm text-slate-600">Contact us 24/7 or get instant online quote</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-purple-600">2</span>
                  </div>
                  <h4 className="font-bold mb-2">We Arrive Fast</h4>
                  <p className="text-sm text-slate-600">30-min average across all Shoreditch</p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-purple-600">3</span>
                  </div>
                  <h4 className="font-bold mb-2">Safe Transport</h4>
                  <p className="text-sm text-slate-600">Vehicle recovered to your chosen destination</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-purple-900 to-indigo-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need Car Recovery in Shoreditch?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
            Available 24/7 for immediate vehicle recovery and breakdown assistance across Shoreditch E1, E2.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-purple-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-purple-900 text-lg px-8 py-6">
                Get Quote Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
